import { useMemo, useState } from 'react';
import { Calendar, Check, Plus, Search, X } from 'lucide-react';
import type { Lead, LeadSource, LeadStage, LeadTemperature } from '../../../lib/leadsApi';
import { useLeadPipelines, useLeadSourcesInUse, useUpdateLeadById } from '../../../hooks/useLeads';
import { usePeople } from '../../../hooks/usePeople';
import { useI18n } from '../../../contexts/I18nContext';
import { useToast } from '../../tasks/linear/Toast';
import { Popover } from '../../tasks/linear/Popover';
import { Avatar } from '../../tasks/linear/Avatar';
import { CalendarPanel } from '../../ui/DateField';
import {
  SOURCE_OPTIONS,
  TEMPERATURE_DOT,
  TEMPERATURE_OPTIONS,
  leadSourceText,
} from '../leadConstants';

/**
 * CLE-192 — inline property editors for the lead detail sidebar. Each is a
 * trigger that opens a `Popover` and applies the change via `useUpdateLeadById`
 * (the same generic update used by the board). Mirrors the task detail editors
 * but bound to lead fields.
 */

type LeadUpdate = ReturnType<typeof useUpdateLeadById>;

// --- Stage ------------------------------------------------------------------

export function LeadStageEditor({ lead }: { lead: Lead }) {
  const update = useUpdateLeadById();
  const toast = useToast();
  const { t } = useI18n();
  const { data: pipelines } = useLeadPipelines();
  const pipeline = pipelines?.find((p) => p.id === lead.pipelineId);
  const stages = pipeline?.stages ?? [];

  function setStage(stage: LeadStage, close: () => void) {
    close();
    if (stage.id === lead.stageId) return;
    update.mutate(
      { id: lead.id, stageId: stage.id },
      {
        // Won in a sales pipeline: the lead became a client (WON rule).
        onSuccess: (updated) => {
          const tr = updated.transition;
          if (!tr) return;
          toast.show(
            tr.toPipeline
              ? t('leads.won.toast', {
                  title: updated.title,
                  client: tr.clientName,
                  pipeline: tr.toPipeline.name,
                })
              : t('leads.won.toastClientOnly', { title: updated.title, client: tr.clientName }),
            { label: t('leads.won.openClient'), href: `/clients/${tr.clientId}` },
          );
        },
      },
    );
  }

  return (
    <Popover
      width={220}
      trigger={({ toggle, ref }) => (
        <button
          ref={ref}
          type="button"
          onClick={toggle}
          className="inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 text-[13px] hover:bg-gray-200"
          aria-label="Schimbă etapa"
        >
          <span
            className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
            style={{ backgroundColor: lead.stage?.color ?? '#6b6b75' }}
          />
          <span className="text-gray-800">{lead.stage?.name ?? 'Etapă'}</span>
        </button>
      )}
    >
      {(close) => (
        <div className="py-1">
          {stages.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => setStage(s, close)}
              className="ui-menu-item justify-between"
            >
              <span className="flex items-center gap-2">
                <span
                  className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                  style={{ backgroundColor: s.color }}
                />
                <span>{s.name}</span>
              </span>
              {s.id === lead.stageId && <Check size={14} className="text-gray-400" />}
            </button>
          ))}
          {stages.length === 0 && <p className="px-3 py-2 text-sm text-gray-400">Nicio etapă.</p>}
        </div>
      )}
    </Popover>
  );
}

// --- Temperature ------------------------------------------------------------

export function LeadTemperatureEditor({ lead }: { lead: Lead }) {
  const update = useUpdateLeadById();

  function setTemp(temperature: LeadTemperature, close: () => void) {
    close();
    if (temperature === lead.temperature) return;
    update.mutate({ id: lead.id, temperature });
  }

  return (
    <Popover
      width={180}
      trigger={({ toggle, ref }) => (
        <button
          ref={ref}
          type="button"
          onClick={toggle}
          className="inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 text-[13px] hover:bg-gray-200"
          aria-label="Schimbă temperatura"
        >
          <span
            className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${TEMPERATURE_DOT[lead.temperature]}`}
          />
          <span className="text-gray-800">
            {TEMPERATURE_OPTIONS.find((o) => o.value === lead.temperature)?.label}
          </span>
        </button>
      )}
    >
      {(close) => (
        <div className="py-1">
          {TEMPERATURE_OPTIONS.map((o) => (
            <button
              key={o.value}
              type="button"
              onClick={() => setTemp(o.value, close)}
              className="ui-menu-item justify-between"
            >
              <span className="flex items-center gap-2">
                <span
                  className={`inline-block h-2.5 w-2.5 shrink-0 rounded-full ${TEMPERATURE_DOT[o.value]}`}
                />
                <span>{o.label}</span>
              </span>
              {o.value === lead.temperature && <Check size={14} className="text-gray-400" />}
            </button>
          ))}
        </div>
      )}
    </Popover>
  );
}

// --- Source -----------------------------------------------------------------

export function LeadSourceEditor({ lead }: { lead: Lead }) {
  const update = useUpdateLeadById();

  function setSource(source: LeadSource, close: () => void) {
    close();
    if (source === lead.source && !lead.sourceLabel) return;
    update.mutate({ id: lead.id, source });
  }

  function setCustom(label: string, close: () => void) {
    close();
    if (label === lead.sourceLabel) return;
    update.mutate({ id: lead.id, sourceLabel: label });
  }

  return (
    <Popover
      width={240}
      trigger={({ toggle, ref }) => (
        <button
          ref={ref}
          type="button"
          onClick={toggle}
          className="rounded px-1.5 py-0.5 text-left text-[13px] text-gray-800 hover:bg-gray-200"
          aria-label="Schimbă sursa"
        >
          {leadSourceText(lead)}
        </button>
      )}
    >
      {(close) => (
        <SourceMenu
          current={lead}
          onPick={(s) => setSource(s, close)}
          onCustom={(l) => setCustom(l, close)}
        />
      )}
    </Popover>
  );
}

/**
 * Built-in sources, the ones typed on other leads, and a field to type a new
 * one. Shared by the sidebar editor and the create modal.
 */
export function SourceMenu({
  current,
  onPick,
  onCustom,
}: {
  current: { source: LeadSource; sourceLabel?: string | null };
  onPick: (source: LeadSource) => void;
  onCustom: (label: string) => void;
}) {
  const custom = useLeadSourcesInUse().data?.sources;
  const [query, setQuery] = useState('');
  const q = query.trim();
  const ql = q.toLowerCase();

  const builtIn = SOURCE_OPTIONS.filter((o) => !ql || o.label.toLowerCase().includes(ql));
  const typed = (custom ?? []).filter((c) => !ql || c.label.toLowerCase().includes(ql));
  const exists =
    SOURCE_OPTIONS.some((o) => o.label.toLowerCase() === ql) ||
    (custom ?? []).some((c) => c.label.toLowerCase() === ql);

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <Search size={14} className="text-gray-400" />
        <input
          autoFocus
          value={query}
          maxLength={60}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' || !q) return;
            e.preventDefault();
            const hit = SOURCE_OPTIONS.find((o) => o.label.toLowerCase() === ql);
            if (hit) onPick(hit.value);
            else onCustom(custom?.find((c) => c.label.toLowerCase() === ql)?.label ?? q);
          }}
          placeholder="Caută sau scrie o sursă…"
          className="w-full bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
        />
      </div>
      <div className="max-h-64 overflow-y-auto py-1">
        {builtIn.map((o) => (
          <button
            key={o.value}
            type="button"
            onClick={() => onPick(o.value)}
            className="ui-menu-item justify-between"
          >
            <span>{o.label}</span>
            {o.value === current.source && !current.sourceLabel && (
              <Check size={14} className="text-gray-400" />
            )}
          </button>
        ))}
        {typed.length > 0 && (
          <div className="mt-1 border-t border-border pt-1">
            {typed.map((c) => (
              <button
                key={c.label}
                type="button"
                onClick={() => onCustom(c.label)}
                className="ui-menu-item justify-between"
              >
                <span className="truncate">{c.label}</span>
                {c.label === current.sourceLabel && <Check size={14} className="text-gray-400" />}
              </button>
            ))}
          </div>
        )}
      </div>
      {q && !exists && (
        <div className="border-t border-border py-1">
          <button type="button" onClick={() => onCustom(q)} className="ui-menu-item">
            <Plus size={14} /> Adaugă sursa „{q}”
          </button>
        </div>
      )}
    </div>
  );
}

// --- Assignee ---------------------------------------------------------------

export function LeadAssigneeEditor({ lead }: { lead: Lead }) {
  const update = useUpdateLeadById();

  return (
    <Popover
      width={260}
      align="end"
      trigger={({ toggle, ref }) => (
        <button
          ref={ref}
          type="button"
          onClick={toggle}
          className="flex items-center gap-2 rounded px-1 py-0.5 hover:bg-gray-200"
          title={lead.assignee ? lead.assignee.name : 'Neatribuit'}
          aria-label="Schimbă responsabilul"
        >
          <Avatar name={lead.assignee?.name ?? null} size={20} />
          <span className="truncate text-[13px] text-gray-700">
            {lead.assignee?.name ?? 'Neatribuit'}
          </span>
        </button>
      )}
    >
      {(close) => <AssigneeMenu lead={lead} update={update} close={close} />}
    </Popover>
  );
}

function AssigneeMenu({
  lead,
  update,
  close,
}: {
  lead: Lead;
  update: LeadUpdate;
  close: () => void;
}) {
  const [query, setQuery] = useState('');
  const { data: people } = usePeople({ active: 'true' });

  // Only people linked to a User account can be a lead assignee, since the
  // assignee FK points at User, not Person.
  const members = useMemo(() => {
    const assignable = (people ?? []).filter((p) => p.userId);
    const q = query.trim().toLowerCase();
    return q ? assignable.filter((p) => p.fullName.toLowerCase().includes(q)) : assignable;
  }, [people, query]);

  function assign(userId: string | null) {
    close();
    if (userId === lead.assigneeId) return;
    update.mutate({ id: lead.id, assigneeId: userId });
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2">
        <Search size={14} className="text-gray-400" />
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Caută membri…"
          className="w-full bg-transparent text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none"
        />
      </div>
      <div className="max-h-64 overflow-y-auto py-1">
        {members.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => assign(p.userId)}
            className="ui-menu-item justify-between"
          >
            <span className="flex items-center gap-2">
              <Avatar name={p.fullName} size={20} />
              <span className="truncate">{p.fullName}</span>
            </span>
            {p.userId === lead.assigneeId && <Check size={14} className="text-gray-400" />}
          </button>
        ))}
        {members.length === 0 && (
          <p className="px-3 py-2 text-sm text-gray-400">Niciun membru găsit.</p>
        )}
      </div>
      <div className="border-t border-border py-1">
        <button type="button" onClick={() => assign(null)} className="ui-menu-item text-gray-500">
          <X size={14} /> Elimină responsabil
        </button>
      </div>
    </div>
  );
}

// --- Value + currency -------------------------------------------------------

const CURRENCIES = ['RON', 'EUR'];

export function LeadValueEditor({ lead }: { lead: Lead }) {
  const update = useUpdateLeadById();
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(lead.value != null ? String(lead.value) : '');

  function commit() {
    setEditing(false);
    const trimmed = text.trim();
    const next = trimmed === '' ? null : Number(trimmed);
    if (next != null && Number.isNaN(next)) {
      setText(lead.value != null ? String(lead.value) : '');
      return;
    }
    if (next === lead.value) return;
    update.mutate({ id: lead.id, value: next });
  }

  function setCurrency(currency: string, close: () => void) {
    close();
    if (currency === lead.currency) return;
    update.mutate({ id: lead.id, currency });
  }

  return (
    <div className="flex items-center gap-1.5">
      {editing ? (
        <input
          autoFocus
          type="number"
          min={0}
          step={100}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commit();
            if (e.key === 'Escape') {
              setText(lead.value != null ? String(lead.value) : '');
              setEditing(false);
            }
          }}
          placeholder="0"
          className="w-24 rounded border border-gray-200 bg-transparent px-1.5 py-0.5 text-[13px] text-gray-800 focus:border-brand-600 focus:outline-none"
        />
      ) : (
        <button
          type="button"
          onClick={() => {
            setText(lead.value != null ? String(lead.value) : '');
            setEditing(true);
          }}
          className="rounded px-1 py-0.5 text-[13px] text-gray-700 hover:bg-gray-200"
        >
          {lead.value != null ? lead.value.toLocaleString('ro-RO') : 'Fără valoare'}
        </button>
      )}

      <Popover
        width={120}
        align="end"
        trigger={({ toggle, ref }) => (
          <button
            ref={ref}
            type="button"
            onClick={toggle}
            className="rounded px-1.5 py-0.5 text-[12px] font-medium text-gray-500 hover:bg-gray-200 hover:text-gray-800"
            aria-label="Schimbă moneda"
          >
            {lead.currency}
          </button>
        )}
      >
        {(close) => (
          <div className="py-1">
            {CURRENCIES.map((c) => (
              <button
                key={c}
                type="button"
                onClick={() => setCurrency(c, close)}
                className="ui-menu-item justify-between"
              >
                <span>{c}</span>
                {c === lead.currency && <Check size={14} className="text-gray-400" />}
              </button>
            ))}
          </div>
        )}
      </Popover>
    </div>
  );
}

// --- Date editor (expected close / last contacted) --------------------------

function toDateInputValue(iso: string): string {
  const d = new Date(iso);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('ro-RO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function LeadDateEditor({
  lead,
  field,
  placeholder,
}: {
  lead: Lead;
  field: 'expectedCloseDate' | 'lastContactedAt';
  placeholder: string;
}) {
  const update = useUpdateLeadById();
  const value = lead[field];

  function pick(iso: string | null, close: () => void) {
    close();
    update.mutate({ id: lead.id, [field]: iso });
  }

  return (
    <Popover
      width={272}
      align="end"
      trigger={({ toggle, ref }) => (
        <button
          ref={ref}
          type="button"
          onClick={toggle}
          className="inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 text-[13px] hover:bg-gray-200"
          aria-label={`Schimbă ${placeholder.toLowerCase()}`}
        >
          <Calendar size={13} className="text-gray-500" />
          <span className={value ? 'text-gray-700' : 'text-gray-400'}>
            {value ? formatDate(value) : placeholder}
          </span>
        </button>
      )}
    >
      {(close) => (
        <div className="flex flex-col">
          {/* Inlined rather than a nested `DateField`: this panel is already a
              Popover portal, and a second one would read as an outside click. */}
          <CalendarPanel
            value={value ? toDateInputValue(value) : ''}
            onPick={(key) => {
              const [y, m, d] = key.split('-').map(Number);
              pick(new Date(y, m - 1, d).toISOString(), close);
            }}
          />
          {value && (
            <div className="border-t border-border py-1">
              <button
                type="button"
                onClick={() => pick(null, close)}
                className="ui-menu-item text-gray-500"
              >
                <X size={14} /> Golește
              </button>
            </div>
          )}
        </div>
      )}
    </Popover>
  );
}

// --- Free-text field (contact name/email/phone, company) --------------------

export function LeadTextEditor({
  lead,
  field,
  placeholder,
  type = 'text',
}: {
  lead: Lead;
  field: 'contactName' | 'contactEmail' | 'contactPhone' | 'companyName';
  placeholder: string;
  type?: 'text' | 'email' | 'tel';
}) {
  const update = useUpdateLeadById();
  const value = lead[field];
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value ?? '');

  function commit() {
    setEditing(false);
    const trimmed = text.trim();
    const next = trimmed === '' ? null : trimmed;
    if (next === (value ?? null)) return;
    update.mutate({ id: lead.id, [field]: next });
  }

  if (editing) {
    return (
      <input
        autoFocus
        type={type}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') commit();
          if (e.key === 'Escape') {
            setText(value ?? '');
            setEditing(false);
          }
        }}
        placeholder={placeholder}
        className="w-full rounded border border-gray-200 bg-transparent px-1.5 py-0.5 text-[13px] text-gray-800 focus:border-brand-600 focus:outline-none"
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => {
        setText(value ?? '');
        setEditing(true);
      }}
      className="w-full truncate rounded px-1 py-0.5 text-left text-[13px] text-gray-700 hover:bg-gray-200"
    >
      {value || <span className="text-gray-400">{placeholder}</span>}
    </button>
  );
}
